/**
 * Compass-Rose Grid System
 * 25 positions: center + 8 directions x 3 rings.
 * Single source of truth for all spatial geometry.
 *
 *             NW3     N3     NE3
 *               \      |      /
 *          NW2    \   N2   /    NE2
 *            \      \  |  /      /
 *   W3 - W2 - W1 - center - E1 - E2 - E3
 *            /      /  |  \      \
 *          SW2    /   S2   \    SE2
 *               /      |      \
 *             SW3     S3     SE3
 */

// ============================================================================
// TYPES
// ============================================================================

export type CompassDirection = 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW';
export type Ring = 1 | 2 | 3;

export type PositionId =
  | 'center'
  | 'N1' | 'N2' | 'N3'
  | 'NE1' | 'NE2' | 'NE3'
  | 'E1' | 'E2' | 'E3'
  | 'SE1' | 'SE2' | 'SE3'
  | 'S1' | 'S2' | 'S3'
  | 'SW1' | 'SW2' | 'SW3'
  | 'W1' | 'W2' | 'W3'
  | 'NW1' | 'NW2' | 'NW3';

export type MapTemplate = 'open_field' | 'small_room' | 'corridor' | 'wide_area';

export interface GridCoord {
  x: number;
  y: number;
}

export type RangeBand = 'melee' | 'close' | 'ranged';

// ============================================================================
// CONSTANTS
// ============================================================================

export const COMPASS_DIRECTIONS: CompassDirection[] = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];

/** All 25 position IDs */
export const ALL_POSITIONS: PositionId[] = [
  'center',
  'N1', 'N2', 'N3',
  'NE1', 'NE2', 'NE3',
  'E1', 'E2', 'E3',
  'SE1', 'SE2', 'SE3',
  'S1', 'S2', 'S3',
  'SW1', 'SW2', 'SW3',
  'W1', 'W2', 'W3',
  'NW1', 'NW2', 'NW3',
];

/**
 * 7x7 grid coordinates for rendering.
 * Center is (3,3). Each direction extends outward from center.
 */
export const POSITION_COORDS: Record<PositionId, GridCoord> = {
  center: { x: 3, y: 3 },
  // North (up)
  N1: { x: 3, y: 2 }, N2: { x: 3, y: 1 }, N3: { x: 3, y: 0 },
  // NE (up-right)
  NE1: { x: 4, y: 2 }, NE2: { x: 5, y: 1 }, NE3: { x: 6, y: 0 },
  // East (right)
  E1: { x: 4, y: 3 }, E2: { x: 5, y: 3 }, E3: { x: 6, y: 3 },
  // SE (down-right)
  SE1: { x: 4, y: 4 }, SE2: { x: 5, y: 5 }, SE3: { x: 6, y: 6 },
  // South (down)
  S1: { x: 3, y: 4 }, S2: { x: 3, y: 5 }, S3: { x: 3, y: 6 },
  // SW (down-left)
  SW1: { x: 2, y: 4 }, SW2: { x: 1, y: 5 }, SW3: { x: 0, y: 6 },
  // West (left)
  W1: { x: 2, y: 3 }, W2: { x: 1, y: 3 }, W3: { x: 0, y: 3 },
  // NW (up-left)
  NW1: { x: 2, y: 2 }, NW2: { x: 1, y: 1 }, NW3: { x: 0, y: 0 },
};

// Reverse lookup: "x,y" → PositionId
const COORD_TO_POSITION = new Map<string, PositionId>();
for (const [id, coord] of Object.entries(POSITION_COORDS)) {
  COORD_TO_POSITION.set(`${coord.x},${coord.y}`, id as PositionId);
}

/**
 * Pre-computed adjacency.
 * Rules:
 * - center ↔ all Ring 1 positions
 * - Same spoke: adjacent rings (N1↔N2, N2↔N3)
 * - Same ring: neighboring compass directions (N1↔NE1, N1↔NW1)
 */
export const ADJACENCY_MAP: Record<PositionId, PositionId[]> = buildAdjacencyMap();

function buildAdjacencyMap(): Record<PositionId, PositionId[]> {
  const adj: Record<string, Set<string>> = {};
  for (const pos of ALL_POSITIONS) adj[pos] = new Set();

  const addEdge = (a: PositionId, b: PositionId) => {
    adj[a].add(b);
    adj[b].add(a);
  };

  // center ↔ all Ring 1
  for (const dir of COMPASS_DIRECTIONS) {
    addEdge('center', `${dir}1` as PositionId);
  }

  // Same spoke: ring 1↔2, ring 2↔3
  for (const dir of COMPASS_DIRECTIONS) {
    addEdge(`${dir}1` as PositionId, `${dir}2` as PositionId);
    addEdge(`${dir}2` as PositionId, `${dir}3` as PositionId);
  }

  // Same ring: neighboring directions
  for (let i = 0; i < COMPASS_DIRECTIONS.length; i++) {
    const next = COMPASS_DIRECTIONS[(i + 1) % COMPASS_DIRECTIONS.length];
    const curr = COMPASS_DIRECTIONS[i];
    for (const ring of [1, 2, 3] as Ring[]) {
      addEdge(`${curr}${ring}` as PositionId, `${next}${ring}` as PositionId);
    }
  }

  const result: Record<string, PositionId[]> = {};
  for (const [pos, neighbors] of Object.entries(adj)) {
    result[pos] = Array.from(neighbors) as PositionId[];
  }
  return result as Record<PositionId, PositionId[]>;
}

// ============================================================================
// PRE-COMPUTED DISTANCE TABLE (BFS)
// ============================================================================

const DISTANCE_TABLE: Record<string, number> = {};

function precomputeDistances(): void {
  for (const start of ALL_POSITIONS) {
    // BFS from start
    const dist: Record<string, number> = { [start]: 0 };
    const queue: PositionId[] = [start];
    let qi = 0;
    while (qi < queue.length) {
      const current = queue[qi++];
      for (const neighbor of ADJACENCY_MAP[current]) {
        if (dist[neighbor] === undefined) {
          dist[neighbor] = dist[current] + 1;
          queue.push(neighbor);
        }
      }
    }
    for (const [pos, d] of Object.entries(dist)) {
      DISTANCE_TABLE[`${start}|${pos}`] = d;
    }
  }
}

precomputeDistances();

// ============================================================================
// MAP TEMPLATES
// ============================================================================

/**
 * Each template defines which positions are available for that encounter shape.
 */
export const MAP_TEMPLATES: Record<MapTemplate, PositionId[]> = {
  // All 25 positions — open field, no restrictions
  open_field: [...ALL_POSITIONS],

  // Small room: center + ring 1 only (9 positions)
  small_room: [
    'center',
    'N1', 'NE1', 'E1', 'SE1', 'S1', 'SW1', 'W1', 'NW1',
  ],

  // Corridor: center + N/S spokes only (7 positions)
  corridor: [
    'center',
    'N1', 'N2', 'N3',
    'S1', 'S2', 'S3',
  ],

  // Wide area: center + rings 1-2 (17 positions)
  wide_area: [
    'center',
    'N1', 'N2', 'NE1', 'NE2', 'E1', 'E2', 'SE1', 'SE2',
    'S1', 'S2', 'SW1', 'SW2', 'W1', 'W2', 'NW1', 'NW2',
  ],
};

// ============================================================================
// PUBLIC FUNCTIONS
// ============================================================================

/** Shortest path length between two positions (pre-computed O(1) lookup) */
export function gridDistance(a: PositionId, b: PositionId): number {
  if (a === b) return 0;
  return DISTANCE_TABLE[`${a}|${b}`] ?? Infinity;
}

/** Convert a grid distance to a range band */
export function toRangeBand(distance: number): RangeBand {
  if (distance === 0) return 'melee';
  if (distance === 1) return 'close';
  return 'ranged';
}

/** Get adjacent positions filtered by an available set */
export function getAdjacentPositions(pos: PositionId, availableSet: PositionId[]): PositionId[] {
  const available = new Set(availableSet);
  return ADJACENCY_MAP[pos].filter(n => available.has(n));
}

/**
 * BFS shortest path from `from` to `to`, constrained to `availableSet`.
 * Returns the path INCLUDING `from` and `to`, or empty array if unreachable.
 */
export function shortestPath(from: PositionId, to: PositionId, availableSet: PositionId[]): PositionId[] {
  if (from === to) return [from];
  const available = new Set(availableSet);
  if (!available.has(from) || !available.has(to)) return [];

  const prev: Record<string, PositionId | null> = { [from]: null };
  const queue: PositionId[] = [from];
  let qi = 0;

  while (qi < queue.length) {
    const current = queue[qi++];
    if (current === to) {
      // Reconstruct path
      const path: PositionId[] = [];
      let node: PositionId | null = to;
      while (node !== null) {
        path.unshift(node);
        node = prev[node] ?? null;
      }
      return path;
    }
    for (const neighbor of ADJACENCY_MAP[current]) {
      if (available.has(neighbor) && prev[neighbor] === undefined) {
        prev[neighbor] = current;
        queue.push(neighbor);
      }
    }
  }

  return []; // Unreachable
}

/**
 * One step toward `toward` from `from`, constrained to available positions.
 * Returns `from` if no move is possible (e.g., already there or blocked).
 */
export function stepToward(from: PositionId, toward: PositionId, availableSet: PositionId[]): PositionId {
  if (from === toward) return from;
  const path = shortestPath(from, toward, availableSet);
  return path.length >= 2 ? path[1] : from;
}

/**
 * One step away from `awayFrom` starting at `from`, constrained to available positions.
 * Picks the adjacent position that maximizes distance from `awayFrom`.
 * Returns `from` if at map edge or blocked.
 */
export function stepAway(from: PositionId, awayFrom: PositionId, availableSet: PositionId[]): PositionId {
  const adjacent = getAdjacentPositions(from, availableSet);
  if (adjacent.length === 0) return from;

  const currentDist = gridDistance(from, awayFrom);
  let best = from;
  let bestDist = currentDist;

  for (const pos of adjacent) {
    const d = gridDistance(pos, awayFrom);
    if (d > bestDist) {
      bestDist = d;
      best = pos;
    }
  }

  return best;
}

/** Reverse lookup from grid coordinates to PositionId */
export function coordToPositionId(x: number, y: number): PositionId | null {
  return COORD_TO_POSITION.get(`${x},${y}`) ?? null;
}

/** Parse position ID to extract direction and ring (null for center) */
export function parsePositionId(pos: PositionId): { direction: CompassDirection | null; ring: Ring | 0 } {
  if (pos === 'center') return { direction: null, ring: 0 };
  const match = pos.match(/^([A-Z]+)(\d)$/);
  if (!match) return { direction: null, ring: 0 };
  return { direction: match[1] as CompassDirection, ring: parseInt(match[2]) as Ring };
}

/** Human-readable labels for map templates */
export const MAP_TEMPLATE_LABELS: Record<MapTemplate, string> = {
  open_field: 'Open Field',
  small_room: 'Small Room',
  corridor: 'Corridor',
  wide_area: 'Wide Area',
};

/**
 * Clamp a position to the available set, walking down the same spoke.
 * e.g. S3 → S2 → S1 → center → first available
 */
export function clampToAvailable(pos: PositionId, available: PositionId[]): PositionId {
  if (available.includes(pos)) return pos;
  const { direction, ring } = parsePositionId(pos);
  if (direction) {
    for (let r = ring - 1; r >= 1; r--) {
      const fallback = `${direction}${r}` as PositionId;
      if (available.includes(fallback)) return fallback;
    }
  }
  if (available.includes('center')) return 'center';
  return available[0];
}

/** Human-readable label for a position */
export function positionLabel(pos: PositionId): string {
  if (pos === 'center') return 'Center';
  const { direction, ring } = parsePositionId(pos);
  const dirLabels: Record<CompassDirection, string> = {
    N: 'North', NE: 'NE', E: 'East', SE: 'SE',
    S: 'South', SW: 'SW', W: 'West', NW: 'NW',
  };
  return `${dirLabels[direction!]} ${ring}`;
}
